interface DisplayInstrument {
  base: string;
  quote: string;
  label: string;
}

interface TradeStreamInput {
  lastPrice?: number;
  EMA?: number;
  slope?: number; // fraction per minute from server
  bigTrendSlope?: number; // fraction per minute from server
  acceleration?: number; // fraction per minute per second from server
  trendDirection?: 'UP' | 'DOWN';
  intraDayTrendDirection?: 'UP' | 'DOWN';
  stdDev?: number; // fraction from server
  longPressure?: number; // 0-100 from server
  shortPressure?: number; // 0-100 from server
  lastByteActivity?: string[]; // 8-bit tick activity
  cache?: string; // tick sequence buffer
  symbol?: string;
  instrument?: DisplayInstrument;
  [key: string]: unknown;
}

interface DisplayPayload {
  instrument: string;
  timestamp: string;
  priceUsd: number;
  emaUsd: number;
  trend: {
    slopePctPerMin: number; // 0-100 scale
    bigSlopePctPerMin: number; // 0-100 scale
    accelPctPerMinPerSec: number; // 0-100 scale
    direction: 'UP' | 'DOWN';
    intraDayDirection: 'UP' | 'DOWN';
  };
  volatility: {
    stdDevPct_1h: number; // 0-100 scale
  };
  pressure: {
    longPct: number; // 0-100 scale
    shortPct: number; // 0-100 scale
  };
  byteContinuum: string[]; // 8 most recent tick directions
}

// Convert fraction (0-1) to percent (0-100)
function fracToPct(frac: number): number {
  return frac * 100;
}

export function transformForDisplay(data: TradeStreamInput): DisplayPayload | null {
  if (!data.lastPrice) return null;

  return {
    instrument: data.instrument?.label ?? '',
    timestamp: new Date().toISOString(),
    priceUsd: round(data.lastPrice, 2),
    emaUsd: round(data.EMA ?? 0, 2),
    trend: {
      // Server sends fractions, convert to percent
      slopePctPerMin: round(fracToPct(data.slope ?? 0), 5),
      bigSlopePctPerMin: round(fracToPct(data.bigTrendSlope ?? 0), 5),
      accelPctPerMinPerSec: round(fracToPct(data.acceleration ?? 0), 5),
      direction: data.trendDirection ?? 'UP',
      intraDayDirection: data.intraDayTrendDirection ?? 'UP',
    },
    volatility: {
      // Server sends fraction, convert to percent
      stdDevPct_1h: round(fracToPct(data.stdDev ?? 0), 5),
    },
    pressure: {
      // Server already sends 0-100, round to whole numbers for display
      longPct: round(data.longPressure ?? 0, 0),
      shortPct: round(data.shortPressure ?? 0, 0),
    },
    byteContinuum: data.lastByteActivity ?? [],
  };
}

function round(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
